import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { ThumbsUp, ThumbsDown, Star, ExternalLink, FileText } from 'lucide-react';
import { getFile, postFeedback } from '../lib/api';
import { toast } from '../lib/toast';
import type { SearchHit } from '../lib/types';
import type { Theme } from '../lib/shiki';
import { CodeBlock } from './CodeBlock';

interface Props {
  hit: SearchHit;
  query: string;
  focused: boolean;
  expanded: boolean;
  theme: Theme;
  onFocus: () => void;
  onToggleExpand: () => void;
}

/** A single result row. Renders the snippet (Shiki-highlighted), the feedback buttons, and
 *  an inline detail panel (fetched lazily via /api/file) when expanded. */
export function ResultRow({
  hit,
  query,
  focused,
  expanded,
  theme,
  onFocus,
  onToggleExpand,
}: Props) {
  // Fetch the file preview the first time the row is expanded. TanStack Query handles caching,
  // request cancellation on collapse/unmount, and the loading/error state — no hand-rolled
  // fetch-during-render (which caused "cannot update state during render" warnings + races).
  const previewQuery = useQuery({
    queryKey: ['file', hit.result_id],
    queryFn: () =>
      getFile({
        repo: hit.repo,
        path: hit.filename,
        branch: hit.branch,
        start: hit.start_line,
        end: hit.end_line,
        context: 20,
      }),
    enabled: expanded,
  });
  const preview = previewQuery.data;
  const previewLoading = previewQuery.isLoading;
  const previewError = previewQuery.error as Error | null;

  const sendFeedback = async (
    kind: 'result_rating' | 'relevant',
    payload: Record<string, unknown>,
    note: string,
  ) => {
    try {
      await postFeedback(kind, payload);
      toast(note, 'success');
    } catch (e: unknown) {
      toast(`feedback failed: ${(e as Error).message}`, 'error');
    }
  };

  const onRate = (rating: 1 | -1) =>
    sendFeedback(
      'result_rating',
      { result_id: hit.result_id, rating, why: null },
      rating === 1 ? 'thumbs up recorded' : 'thumbs down recorded',
    );

  const onMarkRelevant = () =>
    sendFeedback(
      'relevant',
      {
        query,
        repo: hit.repo,
        file_path: hit.filename,
        start_line: hit.start_line,
        end_line: hit.end_line,
        why: null,
      },
      'marked as relevant',
    );

  const isBridge = hit.source === 'bridge';
  const isBranchOverlay = hit.provenance === 'branch_overlay';

  return (
    <div
      tabIndex={0}
      onFocus={onFocus}
      className={clsx(
        'rounded-lg border border-zinc-200 dark:border-zinc-800 p-3 bg-white dark:bg-zinc-900 outline-none',
        focused && 'focused-row',
      )}
    >
      <div className="flex items-baseline gap-2 mb-2 flex-wrap">
        <span className="text-xs font-mono text-zinc-500 dark:text-zinc-400">{hit.repo}</span>
        <span className="text-xs text-zinc-400">/</span>
        <span className="text-sm font-mono break-all">{hit.filename}</span>
        <span className="text-xs font-mono text-zinc-500 dark:text-zinc-400">
          :{hit.start_line}-{hit.end_line}
        </span>
        <span
          className={clsx(
            'text-[10px] uppercase font-medium tracking-wide px-1 py-0.5 rounded',
            hit.repo_kind === 'backend' && 'bg-amber-100 dark:bg-amber-900 text-amber-900 dark:text-amber-100',
            hit.repo_kind === 'frontend' && 'bg-sky-100 dark:bg-sky-900 text-sky-900 dark:text-sky-100',
            hit.repo_kind === 'schema' && 'bg-violet-100 dark:bg-violet-900 text-violet-900 dark:text-violet-100',
            hit.repo_kind === 'bridge' && 'bg-emerald-100 dark:bg-emerald-900 text-emerald-900 dark:text-emerald-100',
          )}
        >
          {hit.repo_kind}
        </span>
        {isBridge && (
          <span
            className="text-[10px] uppercase font-medium tracking-wide px-1 py-0.5 rounded bg-emerald-100 dark:bg-emerald-900 text-emerald-900 dark:text-emerald-100"
            title="backend↔frontend GraphQL operation bridge"
          >
            cross-stack
          </span>
        )}
        {isBranchOverlay && (
          <span
            className="text-[10px] uppercase font-medium tracking-wide px-1 py-0.5 rounded bg-fuchsia-100 dark:bg-fuchsia-900 text-fuchsia-900 dark:text-fuchsia-100"
            title={`branch overlay: ${hit.branch}`}
          >
            branch:{hit.branch}
          </span>
        )}
        <span className="ml-auto text-sm font-mono tabular-nums text-zinc-700 dark:text-zinc-300">
          {hit.score.toFixed(3)}
        </span>
      </div>

      <CodeBlock code={hit.code} language={hit.language} theme={theme} compact />

      <div className="flex items-center gap-2 mt-2 flex-wrap">
        <button
          type="button"
          onClick={() => onRate(1)}
          className="flex items-center gap-1 text-xs px-2 py-1 rounded border border-zinc-300 dark:border-zinc-700 hover:bg-emerald-50 dark:hover:bg-emerald-950"
          title="rate +1"
        >
          <ThumbsUp size={12} /> good
        </button>
        <button
          type="button"
          onClick={() => onRate(-1)}
          className="flex items-center gap-1 text-xs px-2 py-1 rounded border border-zinc-300 dark:border-zinc-700 hover:bg-rose-50 dark:hover:bg-rose-950"
          title="rate -1"
        >
          <ThumbsDown size={12} /> bad
        </button>
        <button
          type="button"
          onClick={onMarkRelevant}
          className="flex items-center gap-1 text-xs px-2 py-1 rounded border border-zinc-300 dark:border-zinc-700 hover:bg-amber-50 dark:hover:bg-amber-950"
          title="mark relevant"
        >
          <Star size={12} /> relevant
        </button>
        <button
          type="button"
          onClick={onToggleExpand}
          className="flex items-center gap-1 text-xs px-2 py-1 rounded border border-zinc-300 dark:border-zinc-700 hover:bg-zinc-50 dark:hover:bg-zinc-800"
        >
          <FileText size={12} />
          {expanded ? 'hide context' : 'show context'}
        </button>
        <span className="ml-auto text-[10px] font-mono text-zinc-500 dark:text-zinc-400">
          id: {hit.result_id}
        </span>
      </div>

      {expanded && (
        <div className="mt-3 border-t border-zinc-200 dark:border-zinc-800 pt-3">
          {previewLoading && <div className="text-xs text-zinc-500">Loading file…</div>}
          {previewError && (
            <div className="text-xs text-rose-600">
              Failed to load file: {previewError.message}
            </div>
          )}
          {preview && (
            <>
              <div className="flex items-center gap-2 mb-2 text-xs text-zinc-500 dark:text-zinc-400 font-mono">
                <ExternalLink size={12} />
                <span>
                  {preview.repo}/{preview.path}
                  {preview.branch !== 'main' && ` @ ${preview.branch}`}
                </span>
                {preview.truncated && (
                  <span className="text-amber-600 dark:text-amber-400">(truncated)</span>
                )}
              </div>
              <CodeBlock
                code={preview.text}
                language={preview.language ?? hit.language}
                theme={theme}
                startLine={preview.start}
              />
            </>
          )}
        </div>
      )}
    </div>
  );
}
